<template>
    <div>
      <Header />
  
      <!-- ── Back + Page Header ── -->
      <div class="back-row">
        <button class="btn-back" @click="$router.back()">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
  
      <div class="page-header">
        <div>
          <h5 class="page-title">{{ paygrade.name || 'Pay Grade Details' }}</h5>
          <p class="page-sub">
            <router-link :to="$url('/payroll')" class="breadcrumb-link">Payroll</router-link>
            <span class="breadcrumb-sep">/</span>
            <router-link :to="$url('/payroll/paygrades')" class="breadcrumb-link">Pay Grades</router-link>
            <span class="breadcrumb-sep">/</span>
            <span>{{ paygrade.name }}</span>
          </p>
        </div>
      </div>
  
      <!-- ── Loading ── -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading pay grade...</p>
      </div>
  
      <template v-else>
  
        <!-- ══════════════════════════════
             DETAILS CARD
        ══════════════════════════════ -->
        <div class="data-card mb-5">
          <div class="card-header">
            <h6 class="card-title">Pay Grade Details</h6>
            <div class="card-actions">
              <!-- Edit mode buttons -->
              <template v-if="isEditing">
                <button class="btn-outline" @click="cancelEdit">
                  <font-awesome-icon icon="times" /> Cancel
                </button>
                <button class="btn-primary-action" :disabled="crudLoading" @click="saveDetails">
                  <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <font-awesome-icon v-else icon="save" class="me-1" />
                  {{ crudLoading ? 'Saving...' : 'Save Changes' }}
                </button>
              </template>
              <!-- View mode buttons -->
              <template v-else>
                <button class="btn-outline" @click="startEdit">
                  <font-awesome-icon icon="pen" /> Edit Details
                </button>
                <button
                  class="btn-danger-outline"
                  :disabled="!canDelete"
                  :title="!canDelete ? 'Cannot delete a pay grade with assigned components' : 'Delete pay grade'"
                  @click="openDeleteModal"
                >
                  <font-awesome-icon icon="trash" /> Delete
                </button>
              </template>
            </div>
          </div>
  
          <!-- View mode -->
          <div v-if="!isEditing" class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Name</span>
              <span class="detail-value fw-bold">{{ paygrade.name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Basic Salary</span>
              <span class="detail-value fw-bold">{{ currencySymbol }}{{ formatPrice(paygrade.basic_salary) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Minimum Salary</span>
              <span class="detail-value">{{ currencySymbol }}{{ formatPrice(paygrade.min_salary) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Maximum Salary</span>
              <span class="detail-value">{{ currencySymbol }}{{ formatPrice(paygrade.max_salary) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Monthly Tax Rate</span>
              <span class="detail-value">{{ paygrade.tax_rate }}%</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Currency</span>
              <span class="detail-value"><span class="currency-badge">{{ paygrade.currency }}</span></span>
            </div>
            <div class="detail-item detail-item--full">
              <span class="detail-label">Description</span>
              <span class="detail-value">{{ paygrade.description || '—' }}</span>
            </div>
          </div>
  
          <!-- Edit mode -->
          <div v-else class="modal-fields">
            <div class="fields-grid">
              <div class="field-group">
                <label class="field-label">Name <span class="req">*</span></label>
                <input type="text" class="field-input" v-model="editForm.name" required />
              </div>
              <div class="field-group">
                <label class="field-label">Currency <span class="req">*</span></label>
                <select class="field-input field-select" v-model="editForm.currency" required>
                  <option value="NGN">NGN — Nigerian Naira</option>
                  <option value="GBP">GBP — British Pound</option>
                  <option value="USD">USD — US Dollar</option>
                </select>
              </div>
            </div>
            <div class="fields-grid">
              <div class="field-group">
                <label class="field-label">Basic Salary <span class="req">*</span></label>
                <input type="number" class="field-input" v-model="editForm.basic_salary" min="0" @keydown="blockInvalidKeys" required />
              </div>
              <div class="field-group">
                <label class="field-label">Monthly Tax Rate (%)</label>
                <input type="number" class="field-input" v-model="editForm.tax_rate" min="0" @keydown="blockInvalidKeys" />
              </div>
            </div>
            <div class="fields-grid">
              <div class="field-group">
                <label class="field-label">Minimum Salary</label>
                <input type="number" class="field-input" v-model="editForm.min_salary" min="0" @keydown="blockInvalidKeys" />
              </div>
              <div class="field-group">
                <label class="field-label">Maximum Salary</label>
                <input type="number" class="field-input" v-model="editForm.max_salary" min="0" @keydown="blockInvalidKeys" />
              </div>
            </div>
            <div class="field-group">
              <label class="field-label">Description</label>
              <textarea class="field-input" rows="3" v-model="editForm.description"></textarea>
            </div>
          </div>
        </div>
  
        <!-- ══════════════════════════════
             SALARY SUMMARY CARDS
        ══════════════════════════════ -->
        <div class="summary-cards mb-5">
          <div class="summary-card">
            <div class="summary-icon summary-icon--blue">
              <font-awesome-icon icon="money-bill-wave" />
            </div>
            <div>
              <p class="summary-label">Monthly Gross</p>
              <p class="summary-value">{{ currencySymbol }}{{ formatPrice(paygrade.gross_salary) }}</p>
            </div>
          </div>
          <div class="summary-card">
            <div class="summary-icon summary-icon--green">
              <font-awesome-icon icon="coins" />
            </div>
            <div>
              <p class="summary-label">Monthly Net</p>
              <p class="summary-value summary-value--green">{{ currencySymbol }}{{ formatPrice(paygrade.net_salary) }}</p>
            </div>
          </div>
          <div class="summary-card">
            <div class="summary-icon summary-icon--amber">
              <font-awesome-icon icon="calendar-alt" />
            </div>
            <div>
              <p class="summary-label">Annual Gross</p>
              <p class="summary-value">{{ currencySymbol }}{{ formatPrice(paygrade.gross_salary * 12) }}</p>
            </div>
          </div>
          <div class="summary-card summary-card--highlight">
            <div class="summary-icon summary-icon--white">
              <font-awesome-icon icon="coins" />
            </div>
            <div>
              <p class="summary-label summary-label--dark">Annual Net</p>
              <p class="summary-value summary-value--highlight">{{ currencySymbol }}{{ formatPrice(paygrade.net_salary * 12) }}</p>
            </div>
          </div>
        </div>
  
        <!-- ══════════════════════════════
             SALARY COMPONENTS TABLE
        ══════════════════════════════ -->
        <div class="data-card">
          <div class="card-header">
            <div>
              <h6 class="card-title">Salary Components</h6>
              <p class="card-sub">Earnings and deductions for this pay grade</p>
            </div>
            <button class="btn-primary-action" @click="openAddComponentModal">
              <font-awesome-icon icon="plus" /> Add Component
            </button>
          </div>
  
          <!-- Empty -->
          <div v-if="paygrade.salaryComponents.length === 0" class="state-block">
            <div class="state-icon state-icon--blue"><font-awesome-icon icon="coins" /></div>
            <p class="state-title">No components assigned</p>
            <p class="state-sub">Add salary components to define this pay grade's salary structure.</p>
          </div>
  
          <!-- Table -->
          <div v-else class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Calculation</th>
                  <th class="text-right">Value</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <!-- Basic salary row (fixed, non-removable) -->
                <tr>
                  <td class="fw-bold">Basic Salary</td>
                  <td><span class="comp-badge comp-badge--earning">Earning</span></td>
                  <td><span class="comp-badge comp-badge--neutral">Fixed</span></td>
                  <td class="text-right fw-bold">{{ currencySymbol }}{{ formatPrice(paygrade.basic_salary) }}</td>
                  <td class="col-actions">
                    <button class="action-icon-btn action-icon-btn--disabled" disabled title="Basic salary cannot be removed">
                      <font-awesome-icon icon="trash" />
                    </button>
                  </td>
                </tr>
                <!-- Dynamic components -->
                <tr v-for="comp in paygrade.salaryComponents" :key="comp.id">
                  <td class="fw-bold">{{ comp.name }}</td>
                  <td>
                    <span class="comp-badge" :class="comp.type === 'earning' ? 'comp-badge--earning' : 'comp-badge--deduction'">
                      {{ comp.type === 'earning' ? 'Earning' : 'Deduction' }}
                    </span>
                  </td>
                  <td>
                    <span class="comp-badge" :class="comp.calculation_type === 'fixed' ? 'comp-badge--neutral' : 'comp-badge--amber'">
                      {{ comp.calculation_type === 'fixed' ? 'Fixed' : 'Percentage' }}
                    </span>
                  </td>
                  <td class="text-right fw-bold">
                    {{ comp.calculation_type === 'fixed'
                      ? currencySymbol + formatPrice(comp.value)
                      : comp.value + '%' }}
                  </td>
                  <td class="col-actions">
                    <button
                      class="action-icon-btn action-icon-btn--danger"
                      title="Remove component"
                      @click="promptRemoveComponent(comp)"
                    >
                      <font-awesome-icon icon="trash" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
  
      </template><!-- end v-else -->
  
  
      <!-- ══════════════════════════════
           ADD COMPONENT MODAL
      ══════════════════════════════ -->
      <div class="modal fade" id="addComponentModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
          <div class="modal-content modal-clean">
  
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
                <font-awesome-icon icon="plus" />
              </div>
              <div>
                <h6 class="modal-clean-title">Add Salary Components</h6>
                <p class="modal-clean-sub">Select components to assign to this pay grade</p>
              </div>
              <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal"></button>
            </div>
  
            <div class="modal-body">
              <div v-if="loadingComponents" class="state-block" style="padding:24px 0;">
                <div class="spinner-border text-primary" style="width:2rem;height:2rem;" role="status"></div>
                <p class="mt-3 text-muted-cell" style="font-size:13px;">Loading components...</p>
              </div>
              <div v-else-if="unassignedComponents.length === 0" class="state-block" style="padding:20px 0;">
                <p class="text-muted-cell" style="font-size:13px;">All available components are already assigned.</p>
              </div>
              <div v-else class="components-list">
                <div v-for="comp in unassignedComponents" :key="comp.id"
                  class="component-row"
                  :class="{ 'component-row--selected': selectedComponents.includes(comp.id) }">
  
                  <div class="component-top">
                    <input type="checkbox" class="component-checkbox"
                      :checked="selectedComponents.includes(comp.id)"
                      @change="toggleComponent(comp.id)" />
                    <div class="component-info">
                      <p class="component-name">{{ comp.name }}</p>
                      <div class="component-badges">
                        <span class="comp-badge" :class="comp.type === 'earning' ? 'comp-badge--earning' : 'comp-badge--deduction'">
                          {{ comp.type }}
                        </span>
                        <span class="comp-badge comp-badge--neutral">{{ comp.calculation_type }}</span>
                      </div>
                    </div>
                  </div>
  
                  <div v-if="selectedComponents.includes(comp.id)" class="component-value-wrap">
                    <label class="comp-value-label">Custom Value</label>
                    <input type="number" class="field-input comp-value-input"
                      v-model="componentValues[comp.id]"
                      min="0" placeholder="0"
                      @keydown="blockInvalidKeys" />
                  </div>
                </div>
              </div>
            </div>
  
            <div class="modal-footer border-0 pt-0">
              <button type="button" class="btn-outline" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                class="btn-primary-action"
                :disabled="assignLoading || selectedComponents.length === 0"
                @click="assignComponents"
              >
                <span v-if="assignLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <font-awesome-icon v-else icon="check" class="me-1" />
                {{ assignLoading ? 'Assigning...' : 'Assign Components' }}
              </button>
            </div>
  
          </div>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           DELETE PAYGRADE MODAL
      ══════════════════════════════ -->
      <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon modal-icon--red"><font-awesome-icon icon="trash" /></div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">Delete Pay Grade?</p>
              <p class="modal-subtext">Are you sure you want to delete <strong>{{ paygrade.name }}</strong>? This action cannot be undone.</p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--danger" :disabled="crudLoading" @click="deletePaygrade">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <font-awesome-icon v-else icon="trash" class="me-1" />
                {{ crudLoading ? 'Deleting...' : 'Yes, Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           REMOVE COMPONENT MODAL
      ══════════════════════════════ -->
      <div class="modal fade" id="removeComponentModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon modal-icon--red"><font-awesome-icon icon="trash" /></div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">Remove Component?</p>
              <p class="modal-subtext">
                Are you sure you want to remove
                <strong>{{ componentToRemove?.name }}</strong> from this pay grade?
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--danger" :disabled="removeLoading" @click="confirmRemoveComponent">
                <span v-if="removeLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <font-awesome-icon v-else icon="trash" class="me-1" />
                {{ removeLoading ? 'Removing...' : 'Yes, Remove' }}
              </button>
            </div>
          </div>
        </div>
      </div>
  
    </div>
  </template>
  
  
  <script>
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../../lib/reusableMixin.js';
  import { useStore } from 'vuex';
  
  export default {
    name: 'PaygradeDetailsPage',
  
    components: { Header },
  
    mixins: [reusableMixin],
  
    data() {
      return {
        paygrade: {
          id:                '',
          reference:         '',
          name:              '',
          description:       '',
          currency:          'NGN',
          basic_salary:      0,
          min_salary:        0,
          max_salary:        0,
          tax_rate:          0,
          gross_salary:      0,
          net_salary:        0,
          salaryComponents:  [],
          store:  useStore(),
        },
  
        availableComponents: [],
  
        loading:          false,
        loadingComponents: false,
        crudLoading:      false,
        assignLoading:    false,
        removeLoading:    false,
  
        isEditing: false,
  
        editForm: {
          name:         '',
          description:  '',
          basic_salary: '',
          min_salary:   '',
          max_salary:   '',
          tax_rate:     '',
          currency:     'NGN',
        },
  
        // Add component modal state
        selectedComponents: [],
        componentValues:    {},
  
        // Remove component modal state
        componentToRemove: null,
  
        toast: useToast(),
      };
    },
  
    computed: {
      // Currency symbol from paygrade.currency
      currencySymbol() {
        const map = { NGN: '₦', GBP: '£', USD: '$' };
        return map[this.paygrade.currency] || this.paygrade.currency;
      },

      permission() { return this.store.state.getCurrentUserPermission; },
  
      // Pay grade can only be deleted when it has no assigned components
      canDelete() {
        return this.store.state.getCurrentUserPermission.delete_paygrade === 1;
      },
  
      // Components not yet assigned to this paygrade
      unassignedComponents() {
        const assignedIds = this.paygrade.salaryComponents.map(c => c.id);
        return this.availableComponents.filter(c => !assignedIds.includes(c.id));
      },
    },
  
    created() {
      this.fetchPaygrade();
      this.fetchSalaryComponents();
    },
  
    methods: {
      // ── Data fetching ──────────────────────────────────────────────────────
      fetchPaygrade() {
        this.loading = true;
        const id = this.$route.params.id;
        axios.get(`/payroll/paygrade/detail/${id}`)
          .then(({ data }) => {
            const src = data?.data?.pay_grade || data?.data?.paygrade || data?.data || {};
            this.paygrade = this.mapPaygrade(src, id);
          })
          .catch(err => {
            this.toast.error(err.response?.data?.message ?? 'Could not load pay grade.');
          })
          .finally(() => { this.loading = false; });
      },
  
      fetchSalaryComponents() {
        this.loadingComponents = true;
        axios.get('/payroll/salary-component/active')
          .then(({ data }) => {
            const items = data?.data?.data ?? data?.data ?? [];
            this.availableComponents = items.map(this.mapComponent);
          })
          .catch(() => { /* intentionally silent */ })
          .finally(() => { this.loadingComponents = false; });
      },
  
      // ── Data mappers ───────────────────────────────────────────────────────
      mapPaygrade(item, fallbackId) {
        return {
          id:               item?.id           || fallbackId,
          reference:        item?.reference    || fallbackId,
          name:             item?.grade_name   || item?.name        || 'Unknown Pay Grade',
          description:      item?.description  || '',
          currency:         item?.currency     || 'NGN',
          basic_salary:     Number(item?.basic_salary  || 0),
          min_salary:       Number(item?.min_salary    || 0),
          max_salary:       Number(item?.max_salary    || 0),
          tax_rate:         Number(item?.tax_rate      || 0),
          gross_salary:     Number(item?.gross_salary  || 0),
          net_salary:       Number(item?.net_salary    || 0),
          salaryComponents: Array.isArray(item?.components)
            ? item.components.map(this.mapComponent)
            : Array.isArray(item?.salary_components)
              ? item.salary_components.map(this.mapComponent)
              : [],
        };
      },
  
      mapComponent(item) {
        return {
          id:               item?.id,
          name:             item?.component_name || item?.name || '',
          type:             item?.type             || 'earning',
          calculation_type: item?.calculation_type || 'fixed',
          value:            Number(item?.pivot?.custom_value || item?.value || 0),
          is_taxable:       Boolean(item?.is_taxable),
          is_statutory:     Boolean(item?.is_statutory),
        };
      },
  
      // ── Edit ───────────────────────────────────────────────────────────────
      startEdit() {
        Object.assign(this.editForm, {
          name:         this.paygrade.name,
          description:  this.paygrade.description,
          basic_salary: this.paygrade.basic_salary,
          min_salary:   this.paygrade.min_salary,
          max_salary:   this.paygrade.max_salary,
          tax_rate:     this.paygrade.tax_rate,
          currency:     this.paygrade.currency,
        });
        this.isEditing = true;
      },
  
      cancelEdit() {
        this.isEditing = false;
      },
  
      saveDetails() {
        this.crudLoading = true;
        axios.put(`/payroll/paygrade/${this.paygrade.id}`, this.editForm)
          .then(() => {
            this.toast.success('Pay grade updated successfully');
            this.isEditing = false;
            this.fetchPaygrade();
          })
          .catch(err => {
            this.toast.error(err.response?.data?.message ?? 'Could not update pay grade');
          })
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── Delete paygrade ────────────────────────────────────────────────────
      openDeleteModal() {
        window.$('#deleteModal').modal('show');
      },
  
      deletePaygrade() {
        this.crudLoading = true;
        axios.delete(`/payroll/paygrade/${this.paygrade.id}`)
          .then(() => {
            this.toast.success('Pay grade deleted successfully');
            window.$('#deleteModal').modal('hide');
            this.$router.push('/manage-paygrade');
          })
          .catch(err => {
            this.toast.error(err.response?.data?.message ?? 'Could not delete pay grade');
          })
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── Add components ─────────────────────────────────────────────────────
      openAddComponentModal() {
        this.selectedComponents = [];
        this.componentValues    = {};
        window.$('#addComponentModal').modal('show');
      },
  
      toggleComponent(id) {
        if (this.selectedComponents.includes(id)) {
          this.selectedComponents = this.selectedComponents.filter(c => c !== id);
          const vals = { ...this.componentValues };
          delete vals[id];
          this.componentValues = vals;
        } else {
          this.selectedComponents = [...this.selectedComponents, id];
          const comp = this.availableComponents.find(c => c.id === id);
          this.componentValues = { ...this.componentValues, [id]: comp?.value ?? 0 };
        }
      },
  
      async assignComponents() {
        if (!this.selectedComponents.length) return;
        this.assignLoading = true;
  
        // Assign one at a time (matches React's sequential for-loop approach)
        try {
          for (const id of this.selectedComponents) {
            await axios.post(`/payroll/paygrade/assign-component/${this.paygrade.id}`, {
              salary_component_id: id,
              custom_value:        Number(this.componentValues[id] || 0),
            });
          }
          this.toast.success('Salary component(s) assigned successfully');
          window.$('#addComponentModal').modal('hide');
          this.fetchPaygrade();
        } catch (err) {
          this.toast.error(err.response?.data?.message ?? 'Could not assign component(s)');
        } finally {
          this.assignLoading = false;
        }
      },
  
      // ── Remove component ───────────────────────────────────────────────────
      promptRemoveComponent(comp) {
        this.componentToRemove = comp;
        window.$('#removeComponentModal').modal('show');
      },
  
      confirmRemoveComponent() {
        if (!this.componentToRemove) return;
        this.removeLoading = true;
        console.log('++++++', this.componentToRemove.id);
        axios.post(`/payroll/paygrade/detach-component/${this.paygrade.id}`, {
            salary_component_id: this.componentToRemove.id,
        })
          .then(() => {
            this.toast.success('Salary component removed successfully');
            window.$('#removeComponentModal').modal('hide');
            this.componentToRemove = null;
            this.fetchPaygrade();
          })
          .catch(err => {
            this.toast.error(err.response?.data?.message ?? 'Could not remove component');
          })
          .finally(() => { this.removeLoading = false; });
      },
  
      // ── Helpers ────────────────────────────────────────────────────────────
      blockInvalidKeys(e) {
        if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
      },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
  };
  </script>
  
  
  <style scoped>
  /* ── Shared ──────────────────────────────────────────────────────────── */
  .mb-5  { margin-bottom:20px; }
  .me-1  { margin-right:4px; }
  .me-2  { margin-right:8px; }
  .mt-3  { margin-top:12px; }
  .ms-auto { margin-left:auto; }
  .fw-bold { font-weight:700; color:#1a2b45; }
  .text-right { text-align:right; }
  .text-muted-cell { color:#4a5e78; }
  
  /* ── Back row ────────────────────────────────────────────────────────── */
  .back-row { margin-bottom:12px; }
  .btn-back { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; padding:0; transition:color .15s; }
  .btn-back:hover { color:#004AAD; }
  
  /* ── Page header / breadcrumb ────────────────────────────────────────── */
  .page-header  { margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
  .breadcrumb-link { color:#7a90b0; text-decoration:none; transition:color .15s; }
  .breadcrumb-link:hover { color:#004AAD; }
  .breadcrumb-sep { color:#c5d0e0; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action  { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; font-family:inherit; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; font-family:inherit; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-danger-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#e03535; border:1px solid #fecaca; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; font-family:inherit; }
  .btn-danger-outline:hover:not(:disabled) { background:#fff0f0; }
  .btn-danger-outline:disabled { opacity:.4; cursor:not-allowed; }
  
  /* ── Data card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  .card-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; padding:18px 24px; border-bottom:1px solid #e4eaf2; }
  .card-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
  .card-sub    { font-size:13px; color:#7a90b0; margin:3px 0 0; }
  .card-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  
  /* ── Detail grid (view mode) ─────────────────────────────────────────── */
  .detail-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; padding:24px; }
  .detail-item { display:flex; flex-direction:column; gap:4px; }
  .detail-item--full { grid-column:1/-1; }
  .detail-label { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; }
  .detail-value { font-size:15px; color:#1a2b45; }
  
  /* ── Edit form ───────────────────────────────────────────────────────── */
  .modal-fields { display:flex; flex-direction:column; gap:14px; padding:24px; }
  .fields-grid  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .field-group  { display:flex; flex-direction:column; gap:5px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req          { color:#e03535; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-select { appearance:auto; cursor:pointer; }
  textarea.field-input { resize:vertical; }
  
  /* ── Summary cards ───────────────────────────────────────────────────── */
  .summary-cards { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .summary-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:14px; }
  .summary-card--highlight { background:rgba(0,74,173,.06); border-color:#c5d6f5; }
  .summary-icon  { width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
  .summary-icon--blue  { background:rgba(0,74,173,.1); color:#004AAD; }
  .summary-icon--green { background:rgba(22,163,74,.1); color:#16a34a; }
  .summary-icon--amber { background:rgba(234,179,8,.1); color:#ca8a04; }
  .summary-icon--white { background:#fff; color:#004AAD; }
  .summary-label { font-size:12px; color:#7a90b0; margin:0 0 4px; }
  .summary-label--dark { color:#4a5e78; }
  .summary-value { font-size:18px; font-weight:800; color:#1a2b45; margin:0; line-height:1; }
  .summary-value--green     { color:#16a34a; }
  .summary-value--highlight { color:#004AAD; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Table ───────────────────────────────────────────────────────────── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:11px 14px; font-size:13px; vertical-align:middle; }
  .col-actions { width:48px; text-align:center; }
  
  /* ── Component badges ────────────────────────────────────────────────── */
  .comp-badge           { display:inline-block; font-size:11px; font-weight:600; border-radius:20px; padding:2px 9px; }
  .comp-badge--earning  { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
  .comp-badge--deduction{ background:#fff0f0; color:#e03535; border:1px solid #fecaca; }
  .comp-badge--neutral  { background:#f4f7fb; color:#7a90b0; border:1px solid #e4eaf2; }
  .comp-badge--amber    { background:#fffbeb; color:#ca8a04; border:1px solid #fde68a; }
  
  .currency-badge { background:#f0f4f8; color:#4a5e78; font-size:11px; font-weight:700; border-radius:6px; padding:2px 8px; font-family:monospace; }
  
  /* ── Action icon button ──────────────────────────────────────────────── */
  .action-icon-btn { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; margin:0 auto; }
  .action-icon-btn--danger:hover { background:#fff0f0; color:#e03535; border-color:#fecaca; }
  .action-icon-btn--disabled { opacity:.35; cursor:not-allowed; }
  
  /* ── Modals ──────────────────────────────────────────────────────────── */
  .modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; display:flex; flex-direction:column; max-height:92vh; }
  .modal-clean form { display:flex; flex-direction:column; flex:1; overflow:hidden; min-height:0; }
  .modal-clean .modal-body { overflow-y:auto; flex:1; }
  .modal-dialog-scrollable .modal-content { max-height:92vh; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Component list (add modal) ──────────────────────────────────────── */
  .components-list { display:flex; flex-direction:column; gap:8px; padding:4px 0; }
  .component-row { display:flex; flex-direction:column; gap:10px; padding:12px 14px; border:1px solid #e4eaf2; border-radius:10px; transition:background .12s, border-color .12s; }
  .component-row--selected { background:#f4f8ff; border-color:#c5d6f5; }
  .component-row:hover:not(.component-row--selected) { background:#f8fafc; }
  .component-top { display:flex; align-items:flex-start; gap:10px; }
  .component-checkbox { width:16px; height:16px; accent-color:#004AAD; cursor:pointer; margin-top:3px; flex-shrink:0; }
  .component-info { flex:1; min-width:0; }
  .component-name { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 6px; }
  .component-badges { display:flex; gap:6px; flex-wrap:wrap; }
  .component-value-wrap { display:flex; flex-direction:column; gap:4px; padding-left:26px; }
  .comp-value-label { font-size:11px; font-weight:600; color:#7a90b0; }
  .comp-value-input { max-width:180px; padding:7px 10px !important; font-size:13px !important; }
  
  /* ── Confirm modals ──────────────────────────────────────────────────── */
  .modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
  .modal-icon      { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
  .modal-icon--red { background:rgba(224,53,53,.1); color:#e03535; }
  .modal-body-clean { padding:16px 28px 8px; text-align:center; }
  .modal-heading  { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
  .modal-subtext  { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
  .modal-actions  { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
  .modal-btn      { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; transition:background .15s; font-family:inherit; }
  .modal-btn--cancel { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; }
  .modal-btn--danger { background:#e03535; color:#fff; }
  .modal-btn--danger:hover:not(:disabled) { background:#c02828; }
  .modal-btn:disabled { opacity:.6; cursor:not-allowed; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:1024px) { .summary-cards { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:768px)  {
    .detail-grid  { grid-template-columns:1fr 1fr; }
    .summary-cards { grid-template-columns:1fr 1fr; }
    .fields-grid  { grid-template-columns:1fr; }
    .card-header  { flex-direction:column; align-items:flex-start; }
  }
  @media (max-width:480px)  {
    .detail-grid   { grid-template-columns:1fr; }
    .summary-cards { grid-template-columns:1fr; }
    .comp-value-input { max-width:100% !important; }
    .component-value-wrap { padding-left:0; }
  }
  </style>